// Where the signatures go on Alliant's Letter of Notification — found on the
// letter itself, never at fixed positions.
//
// Every LON (SKILLED/UNSKILLED, TRANSFER, CHANGE REQUEST, SKILLED) prints the
// same five places to fill, but not in the same place: the block-hours table
// changes length with the months the PA covers, which moves the signature
// lines, and "agree/disagree" lands wherever the child's name leaves it. So
// each place is found by the words Alliant prints next to it, and the box is
// put on the line those words label:
//
//   "This is to certify that I, ____"      printed name           parent
//   "agree/disagree"                       a tick under the word  parent
//   "Parent or Legal Guardian …"  + Date   signature, date        parent
//   "Primary Caregiver"           + Date   signature, date        parent (same person)
//   "Nursing Agency Representative …" + Date  signature, date     agency (page 2)
//
// Checked against eight real letters of all four kinds (2026-10-05). The
// placement is shown before anything is sent, and a box can still be moved.
//
// Fields are the FieldPlacer shape: fractions of the page, y from the TOP.

import * as pdfjs from 'pdfjs-dist/legacy/build/pdf.mjs';

if (typeof window !== 'undefined') pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

export const AGREEMENT = 'agreement';          // the choice group for agree / disagree
const PARENT = 'client';                        // PARTY.CLIENT — the parent signs both page-1 lines
const AGENCY = 'agency';                        // PARTY.AGENCY — anyone in the office, or a nurse

const ANCHORS = {
  name: /^This is to certify that I,/,
  agreement: /agree\/disagree/,
  parent: /^Parent or Legal Guardian/,
  caregiver: /^Primary Caregiver/,
  nurse: /^Nursing Agency Representative/,
};

// Measured on the letters, in points from the anchor's baseline. The line a
// label names is printed ~19pt above the label; a signature sits on it.
const NAME_DX = 82;            // "This is to certify that I, " is a fixed run of text
const NAME_W = 280;
const LINE_ABOVE = 21;         // box bottom, above the label's baseline
const SIG_W = 240, SIG_H = 26;
const DATE_W = 90, DATE_H = 16;
const TICK = 10;
// Within "agree/disagree" (Times): "agree" is centred at 18.4% of the run,
// "disagree" at 71.1%.
const AGREE_AT = 0.184, DISAGREE_AT = 0.711;

// pages: [{ width, height, items: [{ str, x, y, w }] }] — y is the baseline,
// from the bottom, as pdf.js reports it.
export function lonFieldsFromText(pages) {
  const at = {};
  pages.forEach((pg, page) => pg.items.forEach((it) => {
    const s = String(it.str || '').trim();
    for (const [k, re] of Object.entries(ANCHORS)) if (!at[k] && re.test(s)) at[k] = { ...it, page };
  }));

  const fields = [];
  let n = 0;
  const add = (kind, party, page, x, bottom, w, h, extra = {}) => {
    const { width: W, height: H } = pages[page];
    const hh = Math.min(h, H - 1 - bottom);
    fields.push({ id: `lon${n++}`, kind, party, page, x: x / W, y: (H - bottom - hh) / H, w: w / W, h: hh / H, ...extra });
  };
  // The "Date" printed on the same line as a label, or the right-hand column
  // where Alliant puts it.
  const dateX = (a) => {
    const d = pages[a.page].items.find((it) => String(it.str || '').trim() === 'Date' && Math.abs(it.y - a.y) < 3);
    return d ? d.x : pages[a.page].width * 0.755;
  };
  const line = (a, party) => {
    add('sig', party, a.page, a.x + 2, a.y + LINE_ABOVE, SIG_W, SIG_H);
    add('date', party, a.page, dateX(a), a.y + LINE_ABOVE, DATE_W, DATE_H);
  };

  if (at.name) add('name', PARENT, at.name.page, at.name.x + NAME_DX, at.name.y - 1, NAME_W, 14);
  if (at.agreement) {
    const a = at.agreement;
    add('choice', PARENT, a.page, a.x + a.w * AGREE_AT - TICK / 2, a.y - 3 - TICK, TICK, TICK, { group: AGREEMENT, value: 'agree' });
    add('choice', PARENT, a.page, a.x + a.w * DISAGREE_AT - TICK / 2, a.y - 3 - TICK, TICK, TICK, { group: AGREEMENT, value: 'disagree' });
  }
  if (at.parent) line(at.parent, PARENT);
  if (at.caregiver) line(at.caregiver, PARENT);
  if (at.nurse) line(at.nurse, AGENCY);

  const missing = Object.keys(ANCHORS).filter((k) => !at[k]);
  return { fields, missing, complete: missing.length === 0 };
}

// Read the letter and place its fields. Not a LON (or a scan with no text):
// { fields: [], missing: [all five] } — the sender places the fields by hand.
export async function placeLonFields(bytes) {
  const doc = await pdfjs.getDocument({ data: bytes.slice(0) }).promise;
  const pages = [];
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const vp = page.getViewport({ scale: 1 });
    const { items } = await page.getTextContent();
    pages.push({ width: vp.width, height: vp.height, items: items.map((t) => ({ str: t.str, x: t.transform[4], y: t.transform[5], w: t.width })) });
  }
  return lonFieldsFromText(pages);
}

// The parent's answer to the LON, from a signed slot.
export const agreementOf = (slot) => slot?.choices?.[AGREEMENT] || null;
